import React from 'react';
import {
    Volume2,
    Award,
    Radio
} from 'lucide-react';
import styles from './EnhancedAnalysis.module.css';

const MetricCard = ({ title, value, icon: Icon }) => {
    const percentage = Math.round(value);
    const circumference = 2 * Math.PI * 45; // radius of 45 = diameter of 90
    // eslint-disable-next-line
    const radius = 45;

    // Calculate multiple progress circles
    const innerCircumference = 2 * Math.PI * 35; // smaller radius for inner circle
    const outerCircumference = 2 * Math.PI * 42; // radius for decorative outer circle
    // eslint-disable-next-line
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    const innerStrokeDashoffset = innerCircumference - (percentage / 100) * innerCircumference;
    const outerStrokeDashoffset = outerCircumference - (percentage / 100) * outerCircumference;

    const getScoreColor = (val) => {
        if (val >= 80) return '#059669';
        if (val >= 60) return '#d97706';
        return '#dc2626';
    };

    const getIcon = (metricType) => {
        switch (metricType.toLowerCase()) {
            case 'clarity':
                return Volume2;
            case 'overall':
                return Award;
            case 'pitch':
                return Radio;
            default:
                return Icon;
        }
    };

    const CustomIcon = getIcon(title);
    const color = getScoreColor(percentage);

    return (
        <div className={styles.metricCard}>
            <div className={styles.metricContent}>
                <h3 className={styles.metricTitle}>
                    {title.split('_').map(word =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                </h3>
                <p className={styles.description}>
                    {getDescription(title, value)}
                </p>
            </div>
            <div className={styles.circleWrapper}>
                <svg className={styles.progressRing} viewBox="0 0 120 120">
                    {/* Decorative outer dotted circle */}
                    <circle
                        className={styles.progressRingDots}
                        stroke={color}
                        strokeWidth="2"
                        strokeDasharray="2 4"
                        fill="transparent"
                        r="48"
                        cx="60"
                        cy="60"
                        opacity="0.3"
                    />

                    {/* Outer progress circle */}
                    <circle
                        className={styles.progressRingCircleOuter}
                        stroke={color}
                        strokeWidth="3"
                        fill="transparent"
                        r="42"
                        cx="60"
                        cy="60"
                        style={{
                            strokeDasharray: `${outerCircumference} ${outerCircumference}`,
                            strokeDashoffset: outerStrokeDashoffset
                        }}
                        opacity="0.4"
                    />

                    {/* Main progress circle */}
                    <circle
                        className={styles.progressRingCircle}
                        stroke={color}
                        strokeWidth="8"
                        strokeLinecap="round"
                        fill="transparent"
                        r="35"
                        cx="60"
                        cy="60"
                        style={{
                            strokeDasharray: `${innerCircumference} ${innerCircumference}`,
                            strokeDashoffset: innerStrokeDashoffset
                        }}
                    />

                    {/* Add decorative marks at cardinal points */}
                    {[0, 90, 180, 270].map((angle) => (
                        <line
                            key={angle}
                            x1="60"
                            y1="15"
                            x2="60"
                            y2="20"
                            stroke={color}
                            strokeWidth="3"
                            opacity="0.5"
                            transform={`rotate(${angle} 60 60)`}
                        />
                    ))}

                    {/* Percentage text */}
                    <text
                        x="60"
                        y="60"
                        className={styles.percentageText}
                        style={{ fill: color }}
                    >
                        {percentage}%
                    </text>
                </svg>
                <div
                    className={styles.iconWrapper}
                    style={{ backgroundColor: color }}
                >
                    <CustomIcon className={styles.metricIcon} />
                </div>
            </div>
        </div>
    );
};

const getDescription = (metric, value) => {
    const metricLower = metric.toLowerCase();

    switch (metricLower) {
        case 'clarity':
            if (value >= 80) return "Excellent speech clarity and articulation";
            if (value >= 60) return "Good clarity with room for improvement";
            return "Focus on clearer pronunciation and articulation";

        case 'overall':
            if (value >= 80) return "Outstanding overall presentation performance";
            if (value >= 60) return "Solid overall performance with areas to improve";
            return "Consider working on core presentation elements";

        case 'pitch':
            if (value >= 80) return "Great vocal variety and pitch modulation";
            if (value >= 60) return "Decent pitch variation with room for improvement";
            return "Try to vary your pitch more for engagement";

        default:
            return "No description available";
    }
};

export default MetricCard;